/**
 * Payment detail page (/payment?id=…).
 *
 * Data comes from POST /payments/detail via the paymentDetail slice, mapped to
 * the PaymentDetail view model. Renders the summary, response-detail and
 * execution/artifact reference sections. The landing-page search bar is kept
 * at the top (without the "Beacon Lookup" title) so another lookup can be run
 * without navigating back.
 */
import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Spinner from '@cloudscape-design/components/spinner';
import Link from '@cloudscape-design/components/link';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import {
  fetchPaymentDetail, clearPaymentDetail,
} from 'src/store/slices/paymentDetail/paymentDetailSlice';
import { SearchTabs } from '../search/SearchTabs';
import { PaymentSummary } from './summary/PaymentSummary';
import { PaymentResponses } from './responses/PaymentResponses';
import { PaymentReferences } from './references/PaymentReferences';

export const PaymentDetailPage = () => {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('id') ?? '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: detail, otherLegIds, loading, error, notFound } = useAppSelector(state => state.paymentDetail);

  const load = () => {
    if (!paymentId) return undefined;
    return dispatch(fetchPaymentDetail(paymentId));
  };

  // Load whenever the id changes; abort any in-flight request.
  useEffect(() => {
    const promise = load();
    return () => {
      promise?.abort();
      dispatch(clearPaymentDetail());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId, dispatch]);

  const renderBody = () => {
    if (!paymentId) {
      return (
        <Alert type="warning" header="No payment id">
          Provide a payment id, for example /payment?id=&lt;endToEndId&gt;.
        </Alert>
      );
    }
    if (loading) {
      return (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary">Loading payment {paymentId}…</Box>
        </Box>
      );
    }
    if (error) {
      return (
        <Alert type="error" header="Failed to load payment" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      );
    }
    if (notFound || !detail) {
      return (
        <Alert type="warning" header="Payment not found">
          No payment leg matched <strong>{paymentId}</strong>. Identifiers are matched against
          endToEndId, amazonInternalSignature, requestId and invoiceId.
        </Alert>
      );
    }
    return (
      <SpaceBetween size="l">
        {otherLegIds.length > 0 && (
          <Alert type="info" header={`${otherLegIds.length} other leg(s) matched this identifier`}>
            <SpaceBetween direction="horizontal" size="xs">
              {otherLegIds.map(legId => {
                const href = `/payment?id=${encodeURIComponent(legId)}`;
                return (
                  <Link key={legId} href={href} onFollow={e => { e.preventDefault(); navigate(href); }}>
                    {legId}
                  </Link>
                );
              })}
            </SpaceBetween>
          </Alert>
        )}

        <PaymentSummary />
        <PaymentResponses />
        <PaymentReferences />
      </SpaceBetween>
    );
  };

  return (
    <Box padding={{ horizontal: 'l', top: 'l', bottom: 'xl' }}>
      <SpaceBetween size="l">
        <SearchTabs />
        {renderBody()}
      </SpaceBetween>
    </Box>
  );
};
